/**
 * AgentTrace — the custom "Agent Trace" view (frontend-display §13.3 /
 * frontend-entity-coverage §2.3, AgentRun audit = self/trace) at route
 * `/agent-runs/:id/trace`, on the shared Inspector grammar.
 *
 * Tabbed body for one agent run:
 *   - Context: the context packet JSON (fetched from context_snapshot_url),
 *              rendered with the JsonViewer.
 *   - Prompt:  the rendered prompt text (fetched from prompt_url).
 *   - Output:  the model output, rendered as JSON when it parses as JSON,
 *              otherwise as Markdown (preview).
 *   - Status:  a chronological status timeline (created → started → finished).
 *   - Validation: the output-validation panel (`[PROPOSED]` review surface).
 *   - Error:   the failure message, if any.
 *
 * Header carries the one filled **Retry** action (POST /api/agent-runs/:id/retry)
 * plus ghost links to the Summary inspector and the runs list. The run record is
 * fetched with `useOne`; the three artifact bodies via `useRemoteContent`. All
 * E0 — no Card wrappers; structure comes from the Inspector sections + tabs.
 */
import { useMemo } from "react";
import { useParams } from "react-router-dom";
import { useOne, useNavigation, useGo } from "@refinedev/core";
import { Repeat } from "@/components/ui/icon";
import type { AgentRun } from "@aos/shared";

import { Button } from "@/components/ui/button";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import { JsonViewer } from "@/components/app/JsonViewer";
import { MarkdownEditor } from "@/components/app/MarkdownEditor";
import { Skeleton } from "@/components/ui/skeleton";
import { EmptyState, ErrorState } from "@/components/ui/states";
import {
  Inspector,
  InspectorHeader,
  InspectorSection,
} from "@/components/inspector";
import { ShowPageSkeleton, ShowPageError } from "@/components/inspector/ShowPage";
import {
  formatDateTime,
  orDash,
  parseMaybeJson,
  type RemoteContentState,
  useRemoteContent,
} from "../_shared/runs-ui";
import { useApiAction } from "../_shared/useApiAction";
import { OutputValidation } from "./OutputValidation";

/** Layout-preserving skeleton for a loading trace artifact (never a spinner). */
function RemoteLoading() {
  return (
    <div className="space-y-2 py-1">
      <Skeleton className="h-4 w-2/3" />
      <Skeleton className="h-4 w-full" />
      <Skeleton className="h-4 w-5/6" />
    </div>
  );
}

function RemoteJson({
  state,
  emptyMessage,
}: {
  state: RemoteContentState;
  emptyMessage: string;
}) {
  if (state.status === "idle") {
    return <EmptyState icon="contexts" message={emptyMessage} />;
  }
  if (state.status === "loading") return <RemoteLoading />;
  if (state.status === "error") {
    return <ErrorState message={`Could not load — ${state.error}`} />;
  }
  const { value, isJson } = parseMaybeJson(state.text);
  return isJson ? (
    <JsonViewer data={value} />
  ) : (
    <pre className="whitespace-pre-wrap rounded-md border border-border bg-muted p-3 font-mono text-xs">
      {state.text}
    </pre>
  );
}

function RemotePrompt({ state }: { state: RemoteContentState }) {
  if (state.status === "idle") {
    return <EmptyState icon="documents" message="No prompt stored." />;
  }
  if (state.status === "loading") return <RemoteLoading />;
  if (state.status === "error") {
    return <ErrorState message={`Could not load prompt — ${state.error}`} />;
  }
  return (
    <pre className="whitespace-pre-wrap rounded-md border border-border bg-muted p-3 font-mono text-xs">
      {state.text}
    </pre>
  );
}

function RemoteOutput({ state }: { state: RemoteContentState }) {
  if (state.status === "idle") {
    return <EmptyState icon="resultOk" message="No output yet." />;
  }
  if (state.status === "loading") return <RemoteLoading />;
  if (state.status === "error") {
    return <ErrorState message={`Could not load output — ${state.error}`} />;
  }
  const { value, isJson } = parseMaybeJson(state.text);
  // JSON output (the canonical skill output contract) renders as a tree;
  // free-form text renders as Markdown preview.
  return isJson ? (
    <JsonViewer data={value} />
  ) : (
    <MarkdownEditor value={state.text} readOnly />
  );
}

interface TimelineStep {
  label: string;
  at: string | null;
}

function StatusTimeline({ run }: { run: AgentRun }) {
  const steps = useMemo<TimelineStep[]>(
    () => [
      { label: "Created (queued)", at: run.created_at },
      { label: "Started (running)", at: run.started_at },
      {
        label:
          run.status === "failed"
            ? "Failed"
            : run.status === "cancelled"
              ? "Cancelled"
              : "Completed",
        at: run.finished_at,
      },
    ],
    [run.created_at, run.started_at, run.finished_at, run.status],
  );

  return (
    <ol className="space-y-4">
      {steps.map((step, i) => {
        const reached = Boolean(step.at);
        return (
          <li key={step.label} className="flex gap-3">
            <div className="flex flex-col items-center">
              <span
                className={
                  "mt-1 h-3 w-3 rounded-full " +
                  (reached ? "bg-primary" : "bg-muted")
                }
              />
              {i < steps.length - 1 && (
                <span className="mt-1 h-full w-px flex-1 bg-border" />
              )}
            </div>
            <div className="pb-2">
              <p className="text-sm font-medium">{step.label}</p>
              <p className="text-xs text-muted-foreground">
                {reached ? formatDateTime(step.at) : "—"}
              </p>
            </div>
          </li>
        );
      })}
    </ol>
  );
}

export function AgentTrace() {
  const { id } = useParams<{ id: string }>();
  const { list } = useNavigation();
  const go = useGo();

  const { data, isLoading, isError, error, refetch } = useOne<AgentRun>({
    resource: "agent-runs",
    id,
    queryOptions: { enabled: Boolean(id) },
  });

  const run = data?.data;

  // Hooks must run unconditionally; pass `null` when the run isn't loaded yet.
  const contextState = useRemoteContent(run?.context_snapshot_url ?? null);
  const promptState = useRemoteContent(run?.prompt_url ?? null);
  const outputState = useRemoteContent(run?.output_url ?? null);

  if (isLoading) return <ShowPageSkeleton />;
  if (isError || !run) {
    return (
      <ShowPageError
        message={error?.message ?? "Agent run not found."}
        onRetry={() => void refetch()}
      />
    );
  }

  return (
    <Inspector>
      <InspectorHeader
        title="Agent Trace"
        subtitle="The full execution trace for one agent run — context, prompt, output, and status."
        status={run.status}
        primaryAction={<RetryButton runId={run.id} />}
        actions={
          <>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => go({ to: `/agent-runs/${run.id}` })}
            >
              Summary
            </Button>
            <Button variant="ghost" size="sm" onClick={() => list("agent-runs")}>
              All runs
            </Button>
          </>
        }
      />

      <InspectorSection
        title={`${run.executor_type} · ${orDash(run.skill_id)}`}
        aside={
          run.output_summary ? (
            <span className="max-w-md truncate text-sm text-muted-foreground">
              {run.output_summary}
            </span>
          ) : undefined
        }
      >
        <Tabs defaultValue="context">
          <TabsList>
            <TabsTrigger value="context">Context</TabsTrigger>
            <TabsTrigger value="prompt">Prompt</TabsTrigger>
            <TabsTrigger value="output">Output</TabsTrigger>
            <TabsTrigger value="status">Status</TabsTrigger>
            <TabsTrigger value="validation">Validation</TabsTrigger>
            <TabsTrigger value="error">Error</TabsTrigger>
          </TabsList>

          <TabsContent value="context" className="pt-4">
            <RemoteJson
              state={contextState}
              emptyMessage="No context packet stored."
            />
          </TabsContent>
          <TabsContent value="prompt" className="pt-4">
            <RemotePrompt state={promptState} />
          </TabsContent>
          <TabsContent value="output" className="pt-4">
            <RemoteOutput state={outputState} />
          </TabsContent>
          <TabsContent value="status" className="pt-4">
            <StatusTimeline run={run} />
          </TabsContent>
          <TabsContent value="validation" className="pt-4">
            <OutputValidation run={run} />
          </TabsContent>
          <TabsContent value="error" className="pt-4">
            {run.error ? (
              <pre className="whitespace-pre-wrap rounded-md border border-destructive/40 bg-destructive/10 p-3 font-mono text-xs text-destructive">
                {run.error}
              </pre>
            ) : (
              <EmptyState icon="resultOk" message="No error recorded." />
            )}
          </TabsContent>
        </Tabs>
      </InspectorSection>
    </Inspector>
  );
}

/** Retry button bound to a specific agent run id. */
function RetryButton({ runId }: { runId: string }) {
  const { run, loading } = useAgentRetry();
  return (
    <Button
      size="sm"
      onClick={() => run(runId)}
      disabled={loading}
      data-testid="agent-trace-retry"
    >
      <Repeat className="mr-2 h-4 w-4" />
      {loading ? "Retrying…" : "Retry"}
    </Button>
  );
}

function useAgentRetry() {
  const { run: post, loading } = useApiAction();
  const run = (runId: string) =>
    post(`/agent-runs/${runId}/retry`, {
      invalidateResources: ["agent-runs", "events"],
      successMessage: "Agent run re-dispatched.",
    });
  return { run, loading };
}

export default AgentTrace;
